import * as React from "react";
import Axios from "axios";
import { useUserFetch } from "./useUserFetch";
import { useGraphFetch } from "./useGraphFetch";
import { UserAdminItem } from "../interfaces/UserAdminItem";
import { IsVerbose } from '../helpers/Console';

// Module-level dedup cache survives StrictMode mount/unmount/mount in dev,
// so the same accessToken doesn't trigger two parallel FetchUser requests.
type SessionResult =
    | { kind: 'ok'; user: UserAdminItem; api: string; tenantName: string }
    | { kind: 'err'; message: string; status?: number }
    | { kind: 'cancelled' };

const inflight = new Map<string, Promise<SessionResult>>();

const runSession = async (accessToken: string, fetchUser: (t: string) => Promise<UserAdminItem | undefined>, fetchGraph: (t: string) => void): Promise<SessionResult> => {
    if (!accessToken) return { kind: 'err', message: 'No access token' };
    try {
        const user = await fetchUser(accessToken);
        fetchGraph(accessToken);
        if (user?.API && user?.Domains) {
            let api = user.API[0].APIName;
            let tenantName = user.Domains[0].DomainName;
            if (user.changeTenantEnabled && user.changeTenantDomain) {
                const changeDomain = user.Domains.find(d => d.DomainName.toLowerCase() === user.changeTenantDomain!.toLowerCase());
                if (changeDomain) {
                    api = changeDomain.APIName;
                    tenantName = changeDomain.DomainName;
                }
            }
            return { kind: 'ok', user, api, tenantName };
        }
        return { kind: 'err', message: 'User data is missing API or Domains' };
    } catch (error) {
        if (Axios.isCancel(error)) {
            return { kind: 'cancelled' };
        }
        IsVerbose() && console.log(error);
        return { kind: 'err', message: error instanceof Error ? error.message : String(error), status: (error as { status?: number })?.status };
    }
};

export const useSession = () => {
    // Get fetch functions
    const { FetchUser } = useUserFetch()
    const { FetchFromGraph } = useGraphFetch()

    // Track session state
    const [sessionInitialized, setSessionInitialized] = React.useState(false)

    const LoginSession = async (onUserFetch: ( userData: UserAdminItem, api: string, tenantName: string,) => void, onError: (error: string, status?: number) => void, accessToken: string) => {
        setSessionInitialized(true);
        try {
            // Retry once if the cached in-flight promise resolved as cancelled
            // (StrictMode dev cycle cancels the first FetchUser via the unmounted hook).
            for (let attempt = 0; attempt < 2; attempt++) {
                let promise = inflight.get(accessToken);
                if (!promise) {
                    promise = runSession(accessToken, FetchUser, FetchFromGraph);
                    inflight.set(accessToken, promise);
                    promise.finally(() => {
                        if (inflight.get(accessToken) === promise) {
                            inflight.delete(accessToken);
                        }
                    });
                }
                const result = await promise;
                if (result.kind === 'cancelled') {
                    // Cached promise was cancelled; drop it and retry with this caller's still-mounted hook.
                    if (inflight.get(accessToken) === promise) {
                        inflight.delete(accessToken);
                    }
                    continue;
                }
                if (result.kind === 'ok') {
                    onUserFetch(result.user, result.api, result.tenantName);
                } else {
                    onError(result.message, result.status);
                }
                return;
            }
            onError('Session fetch cancelled');
        } finally {
            setSessionInitialized(false);
        }
    }

    return { LoginSession , sessionInitialized}
}

